/**
 * PanelSiteService — 사이트 목록/검색/선택 로직
 *
 * Pure JS: Vue 의존성 없음. API 호출 + 데이터 가공만 담당.
 */
import { SiteApi } from '../../../apis/site.api';
import siteDesignApi from '../../../apis/panel/site_design_api.js';
import { flattenSiteDesignData, getDataByLevel } from '../../../utils/siteDesignDataFlattener.js';
import { SiteDesignSchema, warnOrThrow } from '../schemas/apiSchemas.js';
import {
  panelPerfMark,
  panelPerfMeasure,
  panelPerfCountSummary
} from '../utils/panelPlanningPerfLog.js';

export function createPanelSiteService() {
  return {
    async fetchSites() {
      panelPerfMark('fetchSites:http:start');
      const params = { size: 1000, page: 0, sort: 'name,asc', keyword: '' };
      const response = await SiteApi.getSite(params);
      panelPerfMeasure('fetchSites:http', 'fetchSites:http:start', {
        count: response?.data?.content?.content?.length ?? 0
      });
      return response?.data?.content?.content || [];
    },

    filterSites(sites, query) {
      const q = (query || '').trim().toLowerCase();
      if (!q) return sites;
      return sites.filter(
        s =>
          (s.name && s.name.toLowerCase().includes(q)) ||
          (s.address && s.address.toLowerCase().includes(q))
      );
    },

    async loadSiteDesign(siteId) {
      panelPerfMark('loadSiteDesign:api', { siteId });
      const siteDesign = await siteDesignApi.getSiteDesign(siteId);
      panelPerfMeasure('loadSiteDesign:api', 'loadSiteDesign:api');

      panelPerfMark('loadSiteDesign:schema');
      warnOrThrow(SiteDesignSchema, siteDesign, 'siteDesignApi.getSiteDesign');
      panelPerfMeasure('loadSiteDesign:schema', 'loadSiteDesign:schema');

      panelPerfMark('loadSiteDesign:flatten');
      const flattened = (await flattenSiteDesignData(siteDesign)) || siteDesign;
      panelPerfMeasure('loadSiteDesign:flatten', 'loadSiteDesign:flatten');

      const sections = getDataByLevel(flattened, 'section');
      const strings = getDataByLevel(flattened, 'string') || [];
      const modules = getDataByLevel(flattened, 'module') || [];
      panelPerfCountSummary('loadSiteDesign:counts', {
        siteId,
        sections: sections?.length ?? 0,
        strings: strings.length,
        modules: modules.length
      });

      return { siteDesign, flattened, sectionId: sections?.[0]?.id || null };
    },

    persistSelectedSiteId(siteId) {
      localStorage.setItem('solvit-panel-site-id', siteId);
    },

    clampFocusIndex(listLength, currentIndex) {
      if (!listLength) return -1;
      if (currentIndex < 0 || currentIndex >= listLength) return 0;
      return currentIndex;
    },

    navigateFocusIndex(currentIndex, listLength, direction) {
      if (!listLength) return -1;
      const raw = currentIndex < 0 ? 0 : currentIndex + direction;
      return Math.max(0, Math.min(raw, listLength - 1));
    }
  };
}
